/**
 * PowerSync-owned entity registry — SINGLE SOURCE OF TRUTH.
 *
 * The set of entity types synchronized into PowerSync local SQLite (clients,
 * quotes, jobs, invoices). Write seams and local storage adapters read this file
 * rather than maintaining separate entity lists.
 *
 * It assigns onto `self`, which exists in window and worker scope. Pages load it
 * through the base bundle.
 *
 * Entity TYPE names are singular ('client'); route families are their plural
 * (/clients, /quotes, /jobs, /invoices) — isPowerSyncEntityPath() bridges the two.
 */
(function (root) {
  'use strict';

  // Singular entity type names — the key the Mercure/entity_changed pipeline uses.
  var POWERSYNC_ENTITIES = ['client', 'quote', 'job', 'invoice'];

  /**
   * Plural collection name for a singular entity type: 'quote' -> 'quotes'.
   * All four entities pluralise with a simple 's' — this is the ONE place that
   * happens. Route prefixes below and any DOM list-container id (e.g.
   * sse-entity-updates.js's list-container gate) derive from this instead of
   * re-inlining `entity + 's'`.
   * @param {string} entity - singular entity type (e.g. 'quote')
   * @returns {string} plural collection name (e.g. 'quotes')
   */
  function collectionForEntity(entity) {
    return entity + 's';
  }

  // Route families: the plural of each entity type.
  var ROUTE_PREFIXES = POWERSYNC_ENTITIES.map(function (entity) {
    return '/' + collectionForEntity(entity);
  });

  /**
   * The PowerSync entity (singular) a pathname belongs to, or null. Bridges the
   * plural route family back to the singular type: '/clients/123' -> 'client'.
   * Boundary-safe — matches the prefix exactly or as a path segment, so '/clientside'
   * does NOT match '/clients'. This is the one place the path<->entity mapping lives;
   * isPowerSyncEntityPath() reads it.
   * @param {string} pathname - URL pathname
   * @returns {?string} singular entity type, or null
   */
  function entityForPath(pathname) {
    for (var i = 0; i < ROUTE_PREFIXES.length; i++) {
      var prefix = ROUTE_PREFIXES[i];
      if (pathname === prefix || pathname.indexOf(prefix + '/') === 0) {
        return POWERSYNC_ENTITIES[i];
      }
    }
    return null;
  }

  /**
   * True for a list route (/clients) or any descendant (/clients/123, /clients/new).
   * @param {string} pathname - URL pathname
   * @returns {boolean}
   */
  function isPowerSyncEntityPath(pathname) {
    return entityForPath(pathname) !== null;
  }

  /**
   * True if the given entity type is synchronized by PowerSync.
   * @param {string} entityType - singular entity type (e.g. 'client')
   * @returns {boolean}
   */
  function isPowerSyncEntity(entityType) {
    return POWERSYNC_ENTITIES.indexOf(entityType) !== -1;
  }

  // ── Singular detail routes ─────────────────────────────────────────────────
  // Detail routes are SINGULAR (/quote/123, details.py:250) — a different shape
  // from the plural list families above (/quotes). Built from the same
  // POWERSYNC_ENTITIES list so callers (skeleton-nav.js's per-entity skeleton
  // lookup today; a future detail shell resolver per
  // docs/plans/OFFLINE_POWERSYNC_ROADMAP.md) don't re-type the entity list a
  // third time.
  var DETAIL_ROUTE_RE = new RegExp('^/(' + POWERSYNC_ENTITIES.join('|') + ')/\\d+$');

  /**
   * The PowerSync entity (singular) a SINGULAR detail pathname belongs to, or
   * null. '/quote/123' -> 'quote'; '/quotes/123' (the plural descendant form,
   * never used by this app) does not match.
   * @param {string} pathname - URL pathname
   * @returns {?string} singular entity type, or null
   */
  function entityForDetailPath(pathname) {
    var m = pathname.match(DETAIL_ROUTE_RE);
    return m ? m[1] : null;
  }

  // ── Per-operation local-first classification ───────────
  // THE single owner of "is this write operation local-first?". Local-first
  // operations must have an independently owned UI surface, durable local
  // storage, and an upload/reconciliation path. Network-first is the ordinary
  // request/response path.
  //
  // This is data consumed by opt-in write seams such as queued document sending.
  // Keys are '<entity>.<operation>' with '*' as the entity wildcard for
  // operations that behave the same on every synchronized entity.
  //
  // PROMOTION CRITERIA — an operation may only move to local-first when ALL hold
  // (agreed with Jeremiah 2026-07-31):
  //   1. Its server apply path is idempotent + replay-safe (rides the
  //      powersync-upload processed-op replay table).
  //   2. A server disagreement at reconcile time is user-visible (the
  //      reconciliation-UX toast), never a silent revert.
  //   3. Failure cannot strand money or entitlements — anything touching
  //      limits, seat caps, or billing stays network-first.
  var LOCAL_FIRST_OPERATIONS = {
    // Status and edit writes stay server-rendered until an SSOT-safe local UI
    // consumer is explicitly designed. Photos and queued sends own separate
    // local surfaces and do not reconstruct entity pages.
    '*.status': false,
    '*.edit': false,
    '*.photo_upload': true,
    // Units and sites are not PowerSync entities, so the wildcard does not
    // reach them; their photo cards queue captures the same way.
    'asset.photo_upload': true,
    'site.photo_upload': true,
    '*.send': true,
    // Explicitly network-first (criterion 3). Listed so the classification is
    // visible here rather than implied by absence.
    '*.billing': false,
    '*.seat_change': false,
    '*.limit_change': false
  };

  /**
   * Is `operation` on `entity` classified local-first? Consults the
   * '<entity>.<operation>' key first, then the '*.<operation>' wildcard.
   * Unlisted operations are network-first — local-first is opt-in only.
   * @param {string} entity - singular entity type (e.g. 'quote')
   * @param {string} operation - operation key (e.g. 'status', 'send')
   * @returns {boolean}
   */
  function isLocalFirstOperation(entity, operation) {
    var specific = entity + '.' + operation;
    if (Object.prototype.hasOwnProperty.call(LOCAL_FIRST_OPERATIONS, specific)) {
      return LOCAL_FIRST_OPERATIONS[specific] === true;
    }
    var wildcard = '*.' + operation;
    if (Object.prototype.hasOwnProperty.call(LOCAL_FIRST_OPERATIONS, wildcard)) {
      // Wildcard grants apply only to PowerSync-owned entities.
      return LOCAL_FIRST_OPERATIONS[wildcard] === true && isPowerSyncEntity(entity);
    }
    return false;
  }

  root.POWERSYNC_ENTITIES = POWERSYNC_ENTITIES;
  root.collectionForEntity = collectionForEntity;
  root.entityForPath = entityForPath;
  root.isPowerSyncEntityPath = isPowerSyncEntityPath;
  root.isPowerSyncEntity = isPowerSyncEntity;
  root.isLocalFirstOperation = isLocalFirstOperation;
  root.entityForDetailPath = entityForDetailPath;
})(typeof self !== 'undefined' ? self : this);
